<template>
  <div class="list-page-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-timer"></i>
          <span>{{ $t('system.jobManagementTips') }}</span>
        </div>

        <div class="action-wrapper">
          <el-button type="primary" @click="handleAdd" icon="el-icon-plus" size="small" v-if="hasPermission('sys:job:save')">
            {{ $t('common.add') }}
          </el-button>
          <el-button type="danger" @click="handleBatchDelete" :disabled="selectedIds.length === 0" icon="el-icon-delete" size="small" v-if="hasPermission('sys:job:delete')">
            {{ $t('common.delete') }}
          </el-button>
          <el-button type="warning" @click="handleBatchPause" :disabled="selectedIds.length === 0" icon="el-icon-video-pause" size="small" v-if="hasPermission('sys:job:pause')">
            {{ $t('system.pause') }}
          </el-button>
          <el-button type="success" @click="handleBatchResume" :disabled="selectedIds.length === 0" icon="el-icon-video-play" size="small" v-if="hasPermission('sys:job:resume')">
            {{ $t('system.resume') }}
          </el-button>
          <el-button type="info" @click="handleViewLog" icon="el-icon-tickets" size="small" v-if="hasPermission('sys:job:list')">
            {{ $t('system.jobLog') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <div class="filter-wrapper">
          <div class="filter-content">
            <el-form :inline="true" :model="queryParams" class="filter-form">
              <el-form-item>
                <el-input
                  v-model="queryParams.beanName"
                  :placeholder="$t('system.beanName')"
                  clearable
                  @keyup.enter.native="handleSearch"
                  prefix-icon="el-icon-cpu"
                  style="width: 200px"
                />
              </el-form-item>
              <el-form-item class="search-btns">
                <el-button type="primary" icon="el-icon-search" @click="handleSearch">{{ $t('common.query') }}</el-button>
                <el-button icon="el-icon-refresh" @click="resetQuery">{{ $t('common.reset') }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
    </div>

    <!-- Job List -->
    <el-card class="list-card" shadow="never">
      <el-table
        :data="jobList"
        v-loading="loading"
        height="100%"
        style="width: 100%"
        class="custom-table"
        @selection-change="handleSelectionChange"
        :element-loading-text="$t('common.loading')"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column prop="id" label="ID" width="180" align="center" />
        <el-table-column prop="beanName" :label="$t('system.beanName')" min-width="150" align="center" />
        <el-table-column prop="params" :label="$t('system.jobParams')" min-width="150" show-overflow-tooltip align="center" />
        <el-table-column prop="cronExpression" :label="$t('system.cronExpression')" width="150" align="center" />
        <el-table-column prop="status" :label="$t('common.status')" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 0 ? 'success' : 'info'" size="mini" effect="dark">
              {{ scope.row.status === 0 ? $t('system.statusNormal') : $t('system.statusPaused') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('common.remark')" min-width="150" show-overflow-tooltip align="center" />
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operations')" :width="actionColumnWidth" align="center" fixed="right">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="primary" plain size="mini" icon="el-icon-edit" @click="handleEdit(scope.row)" v-if="hasPermission('sys:job:update')">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="warning" plain size="mini" icon="el-icon-caret-right" @click="handleRunOnce(scope.row)" v-if="hasPermission('sys:job:update')">
                {{ $t('system.runOnce') }}
              </el-button>
              <el-button v-if="scope.row.status === 0 && hasPermission('sys:job:pause')" type="info" plain size="mini" icon="el-icon-video-pause" @click="handlePause(scope.row)">
                {{ $t('system.pause') }}
              </el-button>
              <el-button v-if="scope.row.status !== 0 && hasPermission('sys:job:resume')" type="success" plain size="mini" icon="el-icon-video-play" @click="handleResume(scope.row)">
                {{ $t('system.resume') }}
              </el-button>
              <el-button type="danger" plain size="mini" icon="el-icon-delete" @click="handleDelete(scope.row)" v-if="hasPermission('sys:job:delete')">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="queryParams.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="queryParams.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px" append-to-body>
      <el-form ref="jobForm" :model="jobForm" :rules="rules" label-width="100px">
        <el-form-item :label="$t('system.beanName')" prop="beanName">
          <el-input v-model="jobForm.beanName" :placeholder="$t('system.inputBeanName')" />
        </el-form-item>
        <el-form-item :label="$t('system.jobParams')" prop="params">
          <el-input v-model="jobForm.params" :placeholder="$t('system.inputJobParams')" />
        </el-form-item>
        <el-form-item :label="$t('system.cronExpression')" prop="cronExpression">
          <el-input v-model="jobForm.cronExpression" :placeholder="$t('system.inputCronExpression')" />
          <div class="cron-tip">
            <el-link type="primary" href="https://cron.qqe2.com/" target="_blank">{{ $t('system.cronOnline') }}</el-link>
          </div>
        </el-form-item>
        <el-form-item :label="$t('common.remark')" prop="remark">
          <el-input type="textarea" v-model="jobForm.remark" rows="3" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="submitForm">{{ $t('common.confirm') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getJobList, saveJob, updateJob, deleteJobs, runJobs, pauseJobs, resumeJobs } from '@/apis/system/job'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'JobManagement',
  data() {
    return {
      loading: false,
      jobList: [],
      total: 0,
      queryParams: {
        page: 1,
        limit: 10,
        beanName: ''
      },
      selectedIds: [],
      dialogVisible: false,
      dialogTitle: '',
      jobForm: {
        id: undefined,
        beanName: '',
        params: '',
        cronExpression: '',
        remark: ''
      },
      rules: {
        beanName: [{ required: true, message: this.$t('system.inputBeanName'), trigger: 'blur' }],
        cronExpression: [{ required: true, message: this.$t('system.inputCronExpression'), trigger: 'blur' }]
      }
    }
  },
  created() {
    this.getList()
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:job:update')) count += 2
      if (this.hasPermission('sys:job:pause')) count += 1
      if (this.hasPermission('sys:job:resume')) count += 1
      if (this.hasPermission('sys:job:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 245, 4: 300, 5: 360 }
      return widthMap[count] || 0
    }
  },
  methods: {
    hasPermission,
    getList() {
      this.loading = true
      getJobList(this.queryParams).then(res => {
        this.jobList = res.data.list
        this.total = res.data.total
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.queryParams.page = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        page: 1,
        limit: 10,
        beanName: ''
      }
      this.handleSearch()
    },
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },
    handleSizeChange(val) {
      this.queryParams.limit = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.queryParams.page = val
      this.getList()
    },
    handleAdd() {
      this.dialogTitle = this.$t('common.add')
      this.jobForm = {
        id: undefined,
        beanName: '',
        params: '',
        cronExpression: '',
        remark: ''
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.jobForm.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogTitle = this.$t('common.edit')
      this.jobForm = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.jobForm.clearValidate()
      })
    },
    submitForm() {
      this.$refs.jobForm.validate(valid => {
        if (valid) {
          if (this.jobForm.id) {
            updateJob(this.jobForm).then(() => {
              this.$message.success(this.$t('common.success'))
              this.dialogVisible = false
              this.getList()
            })
          } else {
            saveJob(this.jobForm).then(() => {
              this.$message.success(this.$t('common.success'))
              this.dialogVisible = false
              this.getList()
            })
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('common.confirmDelete'), this.$t('common.tips'), {
        type: 'warning'
      }).then(() => {
        deleteJobs([row.id]).then(() => {
          this.$message.success(this.$t('common.success'))
          this.getList()
        })
      }).catch(() => {})
    },
    handleBatchDelete() {
      this.$confirm(this.$t('common.confirmDelete'), this.$t('common.tips'), {
        type: 'warning'
      }).then(() => {
        deleteJobs(this.selectedIds).then(() => {
          this.$message.success(this.$t('common.success'))
          this.getList()
        })
      }).catch(() => {})
    },
    handleRunOnce(row) {
      this.$confirm(this.$t('system.confirmRunOnce'), this.$t('common.tips'), {
        type: 'warning'
      }).then(() => {
        runJobs([row.id]).then(() => {
          this.$message.success(this.$t('common.success'))
        })
      }).catch(() => {})
    },
    handlePause(row) {
      pauseJobs([row.id]).then(() => {
        this.$message.success(this.$t('common.success'))
        this.getList()
      })
    },
    handleResume(row) {
      resumeJobs([row.id]).then(() => {
        this.$message.success(this.$t('common.success'))
        this.getList()
      })
    },
    handleBatchPause() {
      pauseJobs(this.selectedIds).then(() => {
        this.$message.success(this.$t('common.success'))
        this.getList()
      })
    },
    handleBatchResume() {
      resumeJobs(this.selectedIds).then(() => {
        this.$message.success(this.$t('common.success'))
        this.getList()
      })
    },
    handleViewLog() {
      this.$router.push('/system/job-log')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

/* 任务管理页面特有样式 */
.list-page-container {
  .action-buttons {
    .el-button {
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
    }
  }

  .cron-tip {
    font-size: 12px;
    margin-top: 5px;
  }
}

@media screen and (max-width: 1400px) {
  .list-page-container .action-buttons {
    gap: 4px;

    .el-button {
      min-width: 50px;
      height: 28px;
      font-size: 12px;
      padding: 0 8px;
    }
  }
}

@media screen and (max-width: 768px) {
  .list-page-container {
    padding: 5px;
  }
}
</style>
